import axios from "axios";
import { log } from "@/utils/logger";

export async function sendTemplateToExtension(
): Promise<boolean> {
  const extensionBaseUrl = process.env.EXCEL_EXTENSION_URL;
  
  // Validate extension URL
  if (!extensionBaseUrl) {
    log.error("[ExcelExtension] EXCEL_EXTENSION_URL environment variable is not set");
    return false;
  }
  
  const extensionUrl = `${extensionBaseUrl}/api/add-template-sheet`;
  const fileName = 'Custom_Build_Index_Input_Range.xlsm';
  
  log.info("[ExcelExtension] Starting template request", {
    extensionBaseUrl,
    extensionUrl,
    fileName,
  });
  
  // Retry logic: sometimes the first request fails, retry up to 3 times
  const maxRetries = 3;
  const retryDelay = 1000; // 1 second delay between retries (increased for first request)
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      log.info(`[ExcelExtension] Sending built-in template request to extension (attempt ${attempt}/${maxRetries})`, {
        fileName,
        extensionUrl,
        attempt,
      });
      
      // Send simple POST request for built-in template (no templateBase64 needed)
      const response = await axios.post(
        extensionUrl,
        {
          sheetName: "Custom Index - Inputs",
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
          timeout: 30000, // 30 second timeout for file operations
          validateStatus: (status) => status < 500, // Don't throw on 4xx errors
        }
      );
      
      log.info("[ExcelExtension] Successfully sent built-in template request to extension", {
        status: response.status,
        data: response.data,
        attempt,
      });
      
      // Check if the operation was successful
      if (response.data?.success === false) {
        log.warn("[ExcelExtension] Request succeeded but operation failed", {
          error: response.data.error,
          data: response.data,
        });
        return false;
      }
      
      return true;
    } catch (error: any) {
      const errorMessage = error?.message || String(error);
      const status = error?.response?.status;
      const responseData = error?.response?.data;
      const isNetworkError = !error?.response; // No response means network error
      
      log.error(`[ExcelExtension] Failed to send template to extension (attempt ${attempt}/${maxRetries})`, {
        error: errorMessage,
        response: responseData,
        status: status,
        extensionUrl,
        isNetworkError,
        code: error?.code,
      });
      
      // If this was the last attempt, return false
      if (attempt === maxRetries) {
        log.error("[ExcelExtension] All retry attempts exhausted");
        return false;
      }
      
      // Wait before retrying (longer delay for network errors)
      const delay = isNetworkError ? retryDelay * 2 : retryDelay;
      log.info(`[ExcelExtension] Retrying in ${delay}ms...`);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  
  return false;
}

